/**
 * Live-session dashboard — fetches composite payload and renders 6-tab layout.
 * Mobile (<md): horizontal scrollable tab strip on top + content below.
 * Desktop (md+): vertical sidebar tabs left + content right.
 * Tab state synced to ?tab= URL search param.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@client/lib/api-client';
import { useSessionDashboard } from '@client/lib/queries/use-session-dashboard';
import { TabContent } from './tab-content';
import { TabMaterials } from './tab-materials';
import { TabAttendance } from './tab-attendance';
import { TabReview } from './tab-review';
import { TabAssignments } from './tab-assignments';
import { TabTeacherNote } from './tab-teacher-note';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
  { id: 'noi-dung',   label: 'Nội dung' },
  { id: 'hoc-lieu',   label: 'Học liệu' },
  { id: 'diem-danh',  label: 'Điểm danh' },
  { id: 'danh-gia',   label: 'Đánh giá' },
  { id: 'bai-tap',    label: 'Bài tập' },
  { id: 'ghi-chu',    label: 'Ghi chú GV' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const SESSION_STATUS: { [key: string]: { label: string; variant: 'success' | 'outline' } } = {
  open:   { label: 'Đang mở', variant: 'success' },
  closed: { label: 'Đã chốt', variant: 'outline' },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface LiveSessionDashboardProps {
  classId: string;
  sessionId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function LiveSessionDashboard({ classId, sessionId }: LiveSessionDashboardProps) {
  const navigate = useNavigate();

  // Tab state — local (no router search in component; route handles URL)
  const [activeTab, setActiveTab] = useState<TabId>('noi-dung');

  const { data, isLoading, isError, error, refetch } = useSessionDashboard(classId, sessionId);

  // 403 — redirect
  if (isError && error instanceof ApiError && error.status === 403) {
    void navigate({ to: '/403', replace: true });
    return null;
  }

  // Summary counts for header
  const presentCount = data?.enrollments.filter(
    (e) => e.attendanceStatus === 'present',
  ).length ?? 0;
  const totalCount = data?.enrollments.length ?? 0;

  const sessionDate = data?.session.sessionDate
    ? new Date(data.session.sessionDate).toLocaleDateString('vi-VN', {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    : '—';

  const statusMeta = data
    ? (SESSION_STATUS[data.session.status] ?? SESSION_STATUS['open'])
    : null;

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Button
        variant="ghost"
        size="sm"
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground px-0"
        onClick={() => void navigate({ to: '/teacher/classes/$classId/attendance', params: { classId } })}
      >
        <ArrowLeft className="h-4 w-4" />
        Quay lại điểm danh
      </Button>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-10 rounded-lg" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && !(error instanceof ApiError && error.status === 403) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được dữ liệu buổi học.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Main content */}
      {data && (
        <>
          {/* Header card */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-base font-bold text-foreground leading-tight">
                {data.className ?? 'Buổi học'}
              </h1>
              {statusMeta && (
                <Badge variant={statusMeta.variant} className="text-xs">
                  {statusMeta.label}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
              {data.classCode && <span className="font-mono">{data.classCode}</span>}
              <span>{sessionDate}</span>
              {data.session.sessionNumber != null && (
                <span>Buổi {data.session.sessionNumber}</span>
              )}
              {data.lesson && <span>{data.lesson.title}</span>}
              <span>
                Có mặt: {presentCount}/{totalCount} HV
              </span>
            </p>
          </div>

          {/* Tab layout */}
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            {/* Mobile: horizontal scroll strip */}
            <nav
              aria-label="Mục buổi học"
              className="flex w-full overflow-x-auto gap-1 pb-1 scrollbar-none shrink-0
                md:hidden"
            >
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`shrink-0 rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap min-h-[40px]
                    ${activeTab === tab.id
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Desktop: vertical sidebar */}
            <nav
              aria-label="Mục buổi học"
              className="hidden md:flex w-44 shrink-0 flex-col gap-1 border-r border-border pr-2"
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`relative flex min-h-[44px] w-full items-center rounded-md px-3 py-2 text-sm font-medium text-left transition-colors
                      ${isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                      }`}
                  >
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-foreground"
                      />
                    )}
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            {/* Tab content */}
            <div className="min-w-0 flex-1">
              {activeTab === 'noi-dung' && (
                <TabContent lesson={data.lesson} />
              )}
              {activeTab === 'hoc-lieu' && (
                <TabMaterials materials={data.materials} />
              )}
              {activeTab === 'diem-danh' && (
                <TabAttendance
                  classId={classId}
                  session={data.session}
                  enrollments={data.enrollments}
                />
              )}
              {activeTab === 'danh-gia' && (
                <TabReview
                  classId={classId}
                  sessionId={sessionId}
                  enrollments={data.enrollments}
                />
              )}
              {activeTab === 'bai-tap' && (
                <TabAssignments
                  classId={classId}
                  sessionId={sessionId}
                  assignments={data.assignments}
                />
              )}
              {activeTab === 'ghi-chu' && (
                <TabTeacherNote
                  classId={classId}
                  sessionId={sessionId}
                  attendanceSessionId={data.session.id}
                  initialNote={data.session.teacherNote}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
